import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { WordItem } from '../types';
import {
  CustomWordItem,
  MAX_CUSTOM_WORDS,
  getCustomWords,
  deleteCustomWord,
  clearAllCustomWords,
  subscribeCustomWords,
  syncPlayerVocabularyFromServer,
  resolveCurrentPlayerId,
} from '../utils/customVocabularyStorage';
import { getActivePlayerProfile } from '../utils/saveManager';
import { playSound } from '../audio';
import { isFavoriteWord, toggleFavoriteWord } from '../utils/favorites';
import { CustomWordEditorModal } from './CustomWordEditorModal';
import { CustomQuizChallengeModal } from './CustomQuizChallengeModal';
import { ConjugationBox } from './ConjugationBox';
import {
  Plus,
  Sparkles,
  Edit3,
  Trash2,
  Heart,
  Search,
  BookOpen,
  User,
  ShieldCheck,
  HardDrive,
} from 'lucide-react';

interface CustomVocabularyTabProps {
  allWords: WordItem[];
  onAddScore?: (xp: number) => void;
  playerId?: string;
  playerName?: string;
}

export const CustomVocabularyTab: React.FC<CustomVocabularyTabProps> = ({
  allWords,
  onAddScore,
  playerId: explicitPlayerId,
  playerName: explicitPlayerName,
}) => {
  const effectivePlayerId = resolveCurrentPlayerId(explicitPlayerId);
  const activeProfile = getActivePlayerProfile();
  const effectivePlayerName = explicitPlayerName || activeProfile?.name || 'Voyageur';

  const [customWords, setCustomWords] = useState<CustomWordItem[]>(() => getCustomWords(effectivePlayerId));
  const [searchTerm, setSearchTerm] = useState('');
  const [favoriteWordsSet, setFavoriteWordsSet] = useState<Set<string>>(() => {
    const customList = getCustomWords(effectivePlayerId);
    const set = new Set<string>();
    customList.forEach((w) => {
      if (isFavoriteWord(w.kab)) set.add(w.kab);
    });
    return set;
  });

  // Modals
  const [editingWord, setEditingWord] = useState<CustomWordItem | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [wordToDelete, setWordToDelete] = useState<CustomWordItem | null>(null);

  // Sync with server and load words on mount or when player changes
  useEffect(() => {
    setCustomWords(getCustomWords(effectivePlayerId));
    syncPlayerVocabularyFromServer(effectivePlayerId).then((words) => {
      if (words) {
        setCustomWords(words);
      }
    });
  }, [effectivePlayerId]);

  // Subscribe to storage changes
  useEffect(() => {
    const handleUpdate = (detail?: any) => {
      if (detail && detail.playerId && detail.playerId !== effectivePlayerId) {
        return; // Event was for a different account
      }
      const words = getCustomWords(effectivePlayerId);
      setCustomWords(words);
    };
    const unsubscribe = subscribeCustomWords(handleUpdate);
    return () => unsubscribe();
  }, [effectivePlayerId]);

  // Sync favorites
  useEffect(() => {
    const handleFavUpdate = () => {
      const customList = getCustomWords(effectivePlayerId);
      const set = new Set<string>();
      customList.forEach((w) => {
        if (isFavoriteWord(w.kab)) set.add(w.kab);
      });
      setFavoriteWordsSet(set);
    };
    window.addEventListener('kabyle_favorites_updated', handleFavUpdate);
    return () => window.removeEventListener('kabyle_favorites_updated', handleFavUpdate);
  }, [effectivePlayerId]);

  const handleToggleFavorite = useCallback((kab: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const isNow = toggleFavoriteWord(kab);
    playSound('click');
    setFavoriteWordsSet((prev) => {
      const next = new Set(prev);
      if (isNow) next.add(kab);
      else next.delete(kab);
      return next;
    });
  }, []);

  const handleDeleteConfirm = () => {
    if (wordToDelete) {
      playSound('click');
      deleteCustomWord(wordToDelete.id, effectivePlayerId);
      setWordToDelete(null);
    }
  };

  // Filtered words
  const filteredWords = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return customWords;
    return customWords.filter((w) =>
      w.kab.toLowerCase().includes(term) || w.fr.toLowerCase().includes(term)
    );
  }, [customWords, searchTerm]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-parchment font-mono select-none text-stone-900">
      {/* 1. Header & Controls Bar */}
      <div className="bg-[#fcf8f0] border-b border-[#dfd2be] p-2.5 sm:p-4 space-y-2.5 shrink-0 z-10 shadow-xs">
        {/* Top summary row with Player Account Space Badge */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          <div className="space-y-1.5">
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-base sm:text-lg font-black text-stone-900 uppercase tracking-wider flex items-center gap-1.5">
                <span>✍️</span>
                <span>Mon Vocabulaire (Amawal-iw)</span>
              </span>
              
              {/* Account Ownership Badge */}
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300/80 px-2.5 py-0.5 rounded-full text-xs shadow-2xs">
                <User className="w-3.5 h-3.5 text-emerald-700" />
                <span className="text-stone-600 text-[11px]">Compte :</span>
                <span className="font-bold text-emerald-950 text-[11px] tracking-wide">{effectivePlayerName}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-0.5 animate-pulse" title="Espace permanent synchronisé" />
              </div>

              {/* Count pill */}
              <span className="bg-amber-100/90 text-amber-950 text-xs px-2.5 py-0.5 rounded-full border border-amber-300/80 font-black shadow-2xs">
                {customWords.length} / {MAX_CUSTOM_WORDS} mots
              </span>
            </div>

            <div className="flex items-center gap-2 text-[11px] text-stone-600">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
              <span>
                Mots sauvegardés de façon permanente dans l'espace exclusif du profil <strong className="text-amber-900 font-bold">{effectivePlayerName}</strong>.
              </span>
            </div>
          </div>

          {/* Action Buttons: Add Word + Launch Quiz */}
          <div className="flex items-center gap-2 shrink-0">
            {customWords.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setShowQuizModal(true);
                }}
                className="px-3.5 py-2 bg-gradient-to-r from-amber-700 to-amber-800 hover:from-amber-600 hover:to-amber-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-[0_2px_8px_rgba(180,83,9,0.2)] border border-amber-600 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
                title="Lancer le quiz sur mes mots"
              >
                <Sparkles className="w-4 h-4 animate-pulse text-amber-200" />
                <span>Lancer mon Quizz</span>
                <span className="bg-amber-900/60 text-amber-100 px-1.5 py-0.2 rounded-full text-[10px]">
                  {customWords.length}
                </span>
              </button>
            )}

            <button
              type="button"
              disabled={customWords.length >= MAX_CUSTOM_WORDS}
              onClick={() => {
                playSound('click');
                setIsCreatingNew(true);
              }}
              className={`px-3.5 py-2 font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 ${
                customWords.length >= MAX_CUSTOM_WORDS
                  ? 'bg-[#ede4d4] text-stone-400 border border-[#d6c7b0] cursor-not-allowed'
                  : 'bg-gradient-to-r from-emerald-700 to-emerald-800 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-[0_2px_8px_rgba(5,150,105,0.2)] border border-emerald-600 active:scale-95 cursor-pointer'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un mot</span>
            </button>
          </div>
        </div>

        {/* Search & Category Filter */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-1 border-t border-[#dfd2be]">
          {/* Search bar */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={`Rechercher dans les mots de ${effectivePlayerName}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white border border-[#d6c7b2] rounded-lg px-3 py-1.5 pl-8 text-base sm:text-sm text-stone-900 focus:outline-none focus:border-amber-700 placeholder:text-stone-400 font-mono shadow-2xs"
            />
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-stone-400" />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1.5 text-stone-500 hover:text-stone-900 text-xs bg-[#ede4d4] px-1.5 py-0.5 rounded cursor-pointer border border-[#d6c7b0]"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2. Main Content Grid of Custom Words */}
      <div className="flex-1 overflow-y-auto p-2.5 sm:p-4 space-y-3">
        {/* Empty state */}
        {customWords.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-center p-6 bg-[#fffdf9] rounded-2xl border-2 border-dashed border-[#dfd2be] space-y-3 shadow-xs max-w-md mx-auto my-6">
            <div className="w-14 h-14 rounded-full bg-[#ede4d4] border-2 border-amber-700/60 flex items-center justify-center text-2xl shadow-inner">
              📝
            </div>
            <div className="space-y-1">
              <h4 className="text-base font-bold text-stone-900">
                Le carnet de vocabulaire de {effectivePlayerName} est vide
              </h4>
              <p className="text-xs text-stone-600 max-w-sm">
                Ajoutez vos propres mots en kabyle avec leur traduction pour enrichir votre compte et vous entraîner en quiz !
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  setIsCreatingNew(true);
                }}
                className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md flex items-center gap-1.5 border border-emerald-600"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter un premier mot</span>
              </button>
            </div>
          </div>
        ) : filteredWords.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center text-center p-4 text-stone-500 text-xs">
            <span>Aucun mot ne correspond à votre recherche « {searchTerm} ».</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {filteredWords.map((word) => {
              const isFav = favoriteWordsSet.has(word.kab);
              return (
                <div
                  key={word.id}
                  className="bg-white/95 hover:bg-[#fffdf9] border border-[#e5dcce] hover:border-emerald-600 p-3 rounded-xl transition-all flex flex-col justify-between gap-2 shadow-[0_2px_6px_rgba(5,150,105,0.06)] hover:shadow-[0_4px_12px_rgba(5,150,105,0.12)] group relative"
                >
                  {/* Top: Word + Icon + Controls */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 truncate">
                      <span className="text-xl shrink-0">{word.icon || '📝'}</span>
                      <span className="font-black text-stone-900 text-base group-hover:text-emerald-900 transition-colors tracking-wide truncate">
                        {word.kab}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      {/* Favorite button */}
                      <button
                        type="button"
                        onClick={(e) => handleToggleFavorite(word.kab, e)}
                        className={`p-2 min-w-[38px] min-h-[38px] flex items-center justify-center rounded-xl transition-all cursor-pointer border active:scale-95 ${
                          isFav
                            ? 'bg-rose-100 text-rose-700 border-rose-300 shadow-xs'
                            : 'bg-[#f4eee4] text-stone-400 hover:text-rose-600 border-[#dfd2be]'
                        }`}
                        title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                      >
                        <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
                      </button>

                      {/* Edit button */}
                      <button
                        type="button"
                        onClick={() => {
                          playSound('click');
                          setEditingWord(word);
                        }}
                        className="p-2 min-w-[38px] min-h-[38px] flex items-center justify-center bg-[#f4eee4] hover:bg-amber-100 text-stone-700 hover:text-amber-900 rounded-xl border border-[#dfd2be] hover:border-amber-400 cursor-pointer active:scale-95 transition-all"
                        title="Modifier ce mot"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>

                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => setWordToDelete(word)}
                        className="p-2 min-w-[38px] min-h-[38px] flex items-center justify-center bg-rose-50 hover:bg-rose-100 text-rose-700 hover:text-rose-800 rounded-xl border border-rose-200 cursor-pointer active:scale-95 transition-all"
                        title="Supprimer ce mot"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* French Translation */}
                  <div className="flex items-center justify-between text-xs sm:text-sm pt-1.5 border-t border-[#ede4d4]">
                    <span className="italic text-stone-700 font-semibold truncate flex-1">
                      {word.fr ? (word.fr.includes('/') ? word.fr.split('/')[0].trim() : word.fr) : ''}
                    </span>
                    {word.gender && (word.gender === 'm' || word.gender === 'f') && (
                      <span
                        className={`text-[10px] font-black px-2 py-0.5 rounded-md uppercase shrink-0 ${
                          word.gender === 'f'
                            ? 'bg-rose-100 text-rose-900 border border-rose-300'
                            : 'bg-sky-100 text-sky-900 border border-sky-300'
                        }`}
                      >
                        {word.gender === 'f' ? 'fém.' : 'masc.'}
                      </span>
                    )}
                  </div>

                  {/* Plural / Singular Declensions */}
                  {(() => {
                    const showPlural = Boolean(word.pluralKab && word.pluralKab.trim());
                    const showSingular = Boolean(word.singularKab && word.singularKab.trim());
                    if (!showPlural && !showSingular) return null;

                    return (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1.5 text-xs border-t border-[#ede4d4] mt-0.5">
                        {showPlural && (
                          <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300/80 px-2 py-0.5 rounded-md text-amber-950 shadow-2xs">
                            <span className="text-[10px] font-black text-amber-800 uppercase tracking-wider">Pluriel :</span>
                            <strong className="text-stone-900 font-bold">{word.pluralKab}</strong>
                          </div>
                        )}
                        {showSingular && (
                          <div className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-300/80 px-2 py-0.5 rounded-md text-emerald-950 shadow-2xs">
                            <span className="text-[10px] font-black text-emerald-800 uppercase tracking-wider">Singulier :</span>
                            <strong className="text-stone-900 font-bold">{word.singularKab}</strong>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Variants */}
                  {Boolean(word.variants && word.variants.trim()) && (
                    <div className="pt-1.5 text-xs border-t border-[#ede4d4] flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider bg-emerald-100/70 border border-emerald-300/60 px-1.5 py-0.5 rounded">
                        Variante{word.variants!.includes(',') || word.variants!.includes('/') ? 's' : ''} :
                      </span>
                      <span className="text-stone-800 font-medium italic text-xs">
                        {word.variants}
                      </span>
                    </div>
                  )}

                  {/* Conjugation (Inaccompli & Accompli) - Visible uniquement pour la catégorie Verbes */}
                  {((word.type || '').trim() === 'Verbes' || (word.type || '').trim() === "Verbes d'Action" || (word.type || '').toLowerCase().includes('verbe')) &&
                    (Boolean(word.conjugationAccompli && word.conjugationAccompli.trim()) || Boolean(word.conjugationInaccompli && word.conjugationInaccompli.trim())) && (
                    <ConjugationBox
                      inaccompli={word.conjugationInaccompli}
                      accompli={word.conjugationAccompli}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. Word Editor Modal (Creation or Modification) */}
      {(isCreatingNew || editingWord) && (
        <CustomWordEditorModal
          initialWord={editingWord}
          playerId={effectivePlayerId}
          onClose={() => {
            setIsCreatingNew(false);
            setEditingWord(null);
          }}
          onSaved={() => {
            setIsCreatingNew(false);
            setEditingWord(null);
            setCustomWords(getCustomWords(effectivePlayerId));
          }}
        />
      )}

      {/* 4. Custom Quiz Challenge Modal */}
      {showQuizModal && (
        <CustomQuizChallengeModal
          customWords={customWords}
          allWords={allWords}
          onClose={() => setShowQuizModal(false)}
          onAddScore={onAddScore}
        />
      )}

      {/* 5. Delete Confirmation Modal */}
      {wordToDelete && (
        <div className="fixed inset-0 z-[130] bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 font-mono">
          <div className="bg-[#fcf8f0] border-2 border-rose-500 rounded-2xl p-5 max-w-sm w-full space-y-4 shadow-2xl text-center text-stone-900">
            <div className="w-12 h-12 mx-auto bg-rose-100 rounded-full flex items-center justify-center border border-rose-300 text-rose-700">
              <Trash2 className="w-6 h-6" />
            </div>

            <div>
              <h3 className="text-base font-black text-stone-900">Supprimer ce mot ?</h3>
              <p className="text-xs text-stone-600 mt-1">
                Êtes-vous sûr de vouloir supprimer « <span className="text-emerald-800 font-bold">{wordToDelete.kab}</span> » ({wordToDelete.fr}) de l'espace de {effectivePlayerName} ?
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setWordToDelete(null)}
                className="flex-1 py-2 bg-[#ede4d4] hover:bg-[#dfd2be] text-stone-700 rounded-xl text-xs font-bold border border-[#d6c7b0] cursor-pointer"
              >
                Annuler
              </button>

              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="flex-1 py-2 bg-rose-600 hover:bg-rose-500 text-white font-black rounded-xl text-xs uppercase tracking-wider border border-rose-500 shadow-sm cursor-pointer"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
